/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Icon, Popup, Tooltip, VerticalMenu } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import React, { useContext } from 'react';
import descriptors from '../../locales/en-US/descriptors';
import {
  IAccountStatus,
  IUtilityServiceAccountTableProps
} from './IUtilityServiceSettingsProps';
import {
  getUtilityAccountMessageDescription,
  getUtilityAccountMessageKey,
  getUtilityAccountStatusImage,
  getUtilityImageElemement,
  isDeactivateAvailabel,
  isUpdateAction,
  isUtilityProviderEnrolled
} from '../shared/Utils/Utils';
import { useUtilityAccountContext } from '../../context/UtilityAccountContext/UtilityAccountContext';
import UtilityAccountAction from '../../context/UtilityAccountContext/UtilityAccountActions';
import { useTranslation } from '@scuf/localization';

const UtilityServiceAccountTable = (
  props: IUtilityServiceAccountTableProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data: apiData, requestContext } = props;
  const [, dispatch] = useUtilityAccountContext();
  const showModal = () => dispatch(UtilityAccountAction.showUtilityModal());
  const showDeactivateAccountModal = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showDeactivateAccountModal());
  };
  const handleActions = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showReverifyModal());
  };
  const handleUtilityProviderNameClick = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showMeterNameChangeModal());
  };
  const renderServiceProviderName = (celldata: ICellData) => {
    const accountStatusRow: IAccountStatus = celldata.rowData;
    const { providerName, status } = accountStatusRow;
    const isLinkEnabled = isUtilityProviderEnrolled(status);
    return (
      <div>
        {isLinkEnabled ? (
          <Button
            type="link"
            content={providerName}
            onClick={() => handleUtilityProviderNameClick(celldata)}
            className="provider-name-cell"
          ></Button>
        ) : (
          <p className="provider-name-cell">{providerName}</p>
        )}
      </div>
    );
  };
  const renderUtilityType = (celldata: ICellData) => {
    const accountStatusRow: IAccountStatus = celldata.rowData;
    const { utilityType } = accountStatusRow;
    const imgElement = getUtilityImageElemement(utilityType);
    return (
      <span>
        <span className="score-category-Badge">{imgElement}</span>
        <span>{utilityType}</span>
      </span>
    );
  };
  const renderUtilityStatus = (celldata: ICellData) => {
    const accountStatusRow: IAccountStatus = celldata.rowData;
    const { status } = accountStatusRow;
    const imageElement = getUtilityAccountStatusImage(status);
    const messageKey = getUtilityAccountMessageKey(status);
    const messageDescription = getUtilityAccountMessageDescription(status);
    return (
      <span>
        <span className="status-image">{imageElement}</span>
        <span>{t(messageKey)}</span>
        {messageKey !== descriptors.urjanetStatus.ENROLLED &&
          messageKey !== descriptors.urjanetStatus.Active &&
          messageKey !== descriptors.urjanetStatus.DEACTIVATED && (
            <Tooltip
              element={
                <span className="service-account-status-tootip">
                  <Icon name="badge-info" root="common" size="small" />
                </span>
              }
              content={t(messageDescription)}
              position="bottom center"
            />
          )}
      </span>
    );
  };
  const renderUtilityAction = (celldata: ICellData) => {
    if (isUpdateAction(celldata.rowData.status)) {
      return (
        <Button
          type="link"
          content={t(d.UPDATE)}
          onClick={() => handleActions(celldata)}
          className="utility-action update"
        ></Button>
      );
    } else if (isDeactivateAvailabel(celldata.rowData.status)) {
      return (
        <Popup
          className="emaas-utility-popup"
          position="bottom right"
          element={
            <Icon root="common" name="ellipsis-horizontal" size="medium" />
          }
          on="click"
        >
          <VerticalMenu>
            <VerticalMenu>
              <VerticalMenu.Item
                onClick={() => showDeactivateAccountModal(celldata)}
              >
                {t(d.DEACTIVATEACCOUNT)}
              </VerticalMenu.Item>
            </VerticalMenu>
          </VerticalMenu>
        </Popup>
      );
    } else if (celldata.rowData.status == 'Connectivity_Err') {
      return (
        <Button
          type="link"
          content={t(d.REVERIFY)}
          onClick={() => handleActions(celldata)}
          className="utility-action reverify"
        ></Button>
      );
    } else {
      return <></>;
    }
  };
  return (
    <>
      <div
        className={
          apiData && apiData.length > 0
            ? 'utility-account-datatable-container datatable-fixed-height'
            : 'utility-account-datatable-container'
        }
      >
        <DataTable
          data={apiData}
          search={true}
          searchPlaceholder={t(d.SEARCHFOR)}
          className="utility-account-datatable"
        >
          <DataTable.Column
            field="providerName"
            header={t(d.SERVICEPROVIDERNAME)}
            sortable={true}
            renderer={renderServiceProviderName}
          />
          <DataTable.Column field="accountNumber" header={t(d.ACCOUNTNUMBER)} />
          <DataTable.Column
            initialWidth="15%"
            field="utilityType"
            header={t(d.UtilityType)}
            renderer={renderUtilityType}
            sortable={true}
          />
          <DataTable.Column
            field="status"
            header={t(d.STATUS)}
            renderer={renderUtilityStatus}
            sortable={true}
          />
          {requestContext && requestContext.isEditAllowed && (
            <DataTable.Column
              initialWidth="15%"
              field="actions"
              header={t(d.ACTIONS)}
              renderer={renderUtilityAction}
            />
          )}
        </DataTable>
      </div>
      {apiData.length == 0 && (
        <div className="utility-box">
          <div>
            <div className="align-center">{t(d.NOACCOUNTADDEDHEADER)}</div>
            <div className="align-center">{t(d.NOACCOUNTADDEDMESSAGE)}</div>
            <div className="align-center mt-15">
              <Button
                type="primary"
                content={t(d.ADDUTILITYSERVICE)}
                className="utility-button"
                onClick={showModal}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default UtilityServiceAccountTable;
